// Table-like interface to match Dexie API
/* eslint-disable @typescript-eslint/no-explicit-any */

export interface TableInterface<T> {
  put(item: T): Promise<string>;
  put(key: string, value: any): Promise<string>;
  bulkPut(items: T[]): Promise<string[]>;
  get(key: string): Promise<any>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  toArray(): Promise<T[]>;
  where(key: keyof T): {
    equals(value: any): {
      first(): Promise<T | undefined>;
      toArray(): Promise<T[]>;
    };
  };
}

// Native IndexedDB implementation of table interface
class Table<T extends { key: string }> implements TableInterface<T> {
  constructor(private db: IDBDatabase, private storeName: string) {}

  async put(itemOrKey: T | string, value?: any): Promise<string> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      let request: IDBRequest;
      let keyToReturn: string;

      if (typeof itemOrKey === 'string' && value !== undefined) {
        // Direct key-value storage: put(key, value)
        request = store.put(value, itemOrKey);
        keyToReturn = itemOrKey;
      } else {
        // Object storage: put(item)
        const item = itemOrKey as T;
        request = store.put(item);
        keyToReturn = item.key;
      }

      request.onsuccess = (): void => resolve(keyToReturn);
      request.onerror = (): void => reject(request.error);
    });
  }

  async bulkPut(items: T[]): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const keys: string[] = [];
      let hasError = false;

      if (items.length === 0) {
        resolve([]);
        return;
      }

      // Handle transaction completion
      transaction.oncomplete = (): void => {
        if (!hasError) {
          resolve(keys);
        }
      };

      transaction.onerror = (): void => {
        if (!hasError) {
          hasError = true;
          reject(transaction.error);
        }
      };

      // Process each item
      items.forEach((item, index) => {
        const request = store.put(item);

        request.onsuccess = () => {
          keys[index] = item.key;

          // If all operations completed successfully, the transaction.oncomplete will fire
        };

        request.onerror = (): void => {
          if (!hasError) {
            hasError = true;
            reject(request.error);
          }
        };
      });
    });
  }

  async get(key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async toArray(): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  where(key: keyof T) {
    return {
      equals: (value: any) => {
        return {
          first: async (): Promise<T | undefined> => {
            // For key-based queries, use get directly
            if (key === 'key') {
              return this.get(value);
            }
            // For other fields, scan all records
            const allItems = await this.toArray();
            return allItems.find((item) => item[key] === value);
          },

          toArray: async (): Promise<T[]> => {
            if (key === 'key') {
              const item = await this.get(value);
              return item ? [item] : [];
            }
            const allItems = await this.toArray();
            return allItems.filter((item) => item[key] === value);
          },
        };
      },
    };
  }
}

// toolkit.ts
export function dropDatabase(dbName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(dbName);

    request.onsuccess = () => resolve();
    request.onerror = (event) => reject(event);
    request.onblocked = () => reject(new Error('Database deletion blocked'));
  });
}

export { Table };
